import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    TextField,
    Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { Forms } from "@/types/form";
import { Styled } from "../styles";
import { withErrorBoundary } from "@/components/hoc";
import { yupResolver } from "@hookform/resolvers/yup";
import { useResendVerification, useLogin } from "./hooks";
import schemas from "@/utils/schemas";
import { useUser } from "@/context/user";
import constant from "@/utils/constant";
import { useNavigate } from "react-router-dom";
import useCheckEmailStatus from "@/hooks/auth/useCheckEmailStatus";
import useGoogleSign from "@/hooks/auth/useGoogleSign";
import useCountdown from "@/hooks/auth/useCountdown";
import GoogleButton from "@/components/common/google-button";

/**
 * Note: The email and password inputs (Lines 115-139) are controlled using `register` from `react-hook-form` instead of `Controller`
 * due to an issue with Google Chrome autofill and `react-hook-form`'s `Controller`.
 * This workaround resolves the autofill issue.
 *
 * When upgrading `react-hook-form` and Material-UI in the future, check if the autofill issue is resolved
 * and consider reverting back to using `Controller` and the custom input element for consistency.
 */

const Login: React.FC = () => {
    const navigate = useNavigate();
    const {
        control,
        handleSubmit,
        watch,
        register,
        formState: { errors },
    } = useForm<Forms["Login"]>({
        resolver: yupResolver(schemas.login),
        defaultValues: { email: "", password: "", remember: false },
    });

    const loading = useUser(state => state.globalLoading);

    const onResendVerification = useResendVerification();
    const { countdown, counting, startCountdown } = useCountdown();

    const { onLogin, emailVerified, setEmailVerified } = useLogin(startCountdown);
    const onGoogleSign = useGoogleSign();
    useCheckEmailStatus({ check: emailVerified === false });

    if (emailVerified === false) {
        return (
            <Styled.Form>
                <Grid container rowGap={constant.GRID_SPACING}>
                    <Grid item xs={12}>
                        <TextField
                            data-testid="verification-email-input"
                            fullWidth={true}
                            label="Email"
                            type="email"
                            autoComplete="email"
                            value={watch("email")}
                            disabled={true}
                            tabIndex={1}
                        />
                    </Grid>
                    {counting ? (
                        <Grid item xs={12}>
                            <Typography textAlign="center">
                                Try again in {countdown} seconds
                            </Typography>
                        </Grid>
                    ) : null}
                    <Grid item xs={12}>
                        <Button
                            data-testid="verification-resend-button"
                            fullWidth={true}
                            onClick={onResendVerification}
                            disabled={loading || counting}
                            tabIndex={2}
                        >
                            Resend
                        </Button>
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            data-testid="verification-back-button"
                            fullWidth={true}
                            variant="text"
                            type="submit"
                            onClick={() => setEmailVerified(null)}
                            tabIndex={3}
                        >
                            Back
                        </Button>
                    </Grid>
                </Grid>
            </Styled.Form>
        );
    }

    return (
        <Styled.Form onSubmit={handleSubmit(async form => await onLogin(form))}>
            <Grid container rowGap={constant.GRID_SPACING}>
                <Grid item xs={12}>
                    <Typography textAlign="center" variant="h3" marginBottom={0}>
                        Sign in
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <GoogleButton
                        data-testid="login-google-button"
                        onClick={onGoogleSign}
                        style={{ width: "100%" }}
                        tabIndex={1}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Typography marginBottom="10px" variant="body2">
                        Or continue with email:
                    </Typography>
                    <TextField
                        {...register("email")}
                        data-testid="login-email-input"
                        label="Email"
                        type="email"
                        autoComplete="email"
                        tabIndex={2}
                        fullWidth
                        error={!!errors.email}
                        helperText={errors.email?.message}
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        {...register("password")}
                        data-testid="login-password-input"
                        label="Password"
                        type="password"
                        className="sentry-mask"
                        tabIndex={3}
                        fullWidth
                        error={!!errors.password}
                        helperText={errors.password?.message}
                    />
                </Grid>
                <Grid item xs={12}>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        marginBottom="5px"
                    >
                        <Controller
                            name="remember"
                            control={control}
                            defaultValue={false}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={<Checkbox {...field} />}
                                    label={<Typography variant="body2">Remember me</Typography>}
                                />
                            )}
                        />
                        <Button
                            data-testid="login-forgot-password-button"
                            variant="text"
                            onClick={() => navigate("/reset-password")}
                        >
                            Forgot password?
                        </Button>
                    </Box>
                    <Button
                        data-testid="login-button"
                        tabIndex={4}
                        fullWidth={true}
                        type="submit"
                        disabled={loading}
                    >
                        Sign in
                    </Button>
                </Grid>
            </Grid>
        </Styled.Form>
    );
};

export default withErrorBoundary({ component: "Login" })(Login);
